/**
 * Contract shared by `api/proof-metrics.ts` (`view=library`, `view=navigation_counts`) and the browser
 * service. One row per `proof_metrics` record with the simplified ~15-field record plus the audit columns
 * the page shows. Search, filters and sort run in the browser; the server returns every row up to the cap.
 */
import { z } from 'zod';

export const PROOF_LIBRARY_SCAN_CAP = 500 as const;

const nullableText = z.string().nullable();
const nullableDate = z.string().nullable();

export const proofLibraryRowSchema = z.object({
  id: z.string().min(1),
  proof_metric_id: z.string().min(1),
  version: z.number().int().positive(),
  status: z.enum(['draft', 'pending_approval', 'approved', 'ready', 'expired', 'revoked', 'archived']),
  ingest_origin: z.enum(['legacy_manual', 'manual', 'ai_extracted', 'extracted', 'analytics']).nullable(),
  label_short: z.string(),
  claim_text: z.string(),
  value_display: z.string(),
  metric_type: z.string().nullable(),
  proof_type: z.string().nullable(),
  proof_strength: z.enum(['hard_metric', 'case_study', 'survey', 'social_proof', 'qualitative']).nullable(),
  time_window: z.string().nullable(),
  source_type: z.string(),
  source_ref: z.string(),
  source_period_start: nullableDate,
  source_period_end: nullableDate,
  location_ids: z.array(z.string()),
  location_labels: z.array(z.string()),
  anonymization: z.enum(['named', 'anonymized', 'aggregate_only']),
  named_use_ok: z.boolean(),
  channels_allowed: z.array(z.string()),
  value_areas: z.array(z.string()),
  claim_limitations: z.array(z.string()),
  public_slot: nullableText,
  public_payload: z.record(z.unknown()).nullable(),
  expires_at: nullableDate,
  approved_by: nullableText,
  approved_at: nullableText,
  archived_at: nullableText,
  archive_reason: nullableText,
  created_at: z.string(),
  updated_at: z.string(),
  /** Server-computed Store Pulse eligibility for the AI button (plan D10); null when ineligible or Store Pulse is unconfigured (e.g. Preview). */
  store_pulse: z.object({ location_id: z.string(), as_of_week: z.string() }).nullable(),
});

export type ProofLibraryRow = z.infer<typeof proofLibraryRowSchema>;

export const proofLibraryResponseSchema = z.object({
  ok: z.literal(true),
  rows: z.array(proofLibraryRowSchema),
  total: z.number().int().nonnegative(),
  cap: z.literal(PROOF_LIBRARY_SCAN_CAP),
  /** Rows the server could not normalise (never expected); the page shows a short note when non-zero. */
  omitted: z.number().int().nonnegative().default(0),
});

export type ProofLibraryResponse = z.infer<typeof proofLibraryResponseSchema>;

export const proofNavigationCountsSchema = z.object({
  total: z.number().int().nonnegative(),
  ready: z.number().int().nonnegative(),
  suggestions: z.number().int().nonnegative(),
  /** Kept for the existing sub-nav badge contract until B5 removes the draft badge. */
  proofDrafts: z.number().int().nonnegative(),
  needsAttention: z.number().int().nonnegative(),
});

export type ProofNavigationCounts = z.infer<typeof proofNavigationCountsSchema>;

export const proofNavigationCountsResponseSchema = proofNavigationCountsSchema.extend({ ok: z.literal(true) });

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

/**
 * Normalises a raw `proof_metrics` row (with `location_labels` attached) into the library contract.
 * `named_use_ok` falls back to the legacy approval ref until the B2 column exists.
 */
export function toProofLibraryRow(row: Record<string, unknown>): ProofLibraryRow {
  const namedUseOk = typeof row.named_use_ok === 'boolean'
    ? row.named_use_ok
    : typeof row.named_use_approval_ref === 'string' && row.named_use_approval_ref.trim().length > 0;
  return proofLibraryRowSchema.parse({
    id: row.id,
    proof_metric_id: row.proof_metric_id,
    version: row.version,
    status: row.status,
    ingest_origin: row.ingest_origin ?? null,
    label_short: row.label_short ?? '',
    claim_text: row.claim_text ?? '',
    value_display: row.value_display ?? '',
    metric_type: nullableString(row.metric_type),
    proof_type: nullableString(row.proof_type),
    proof_strength: row.proof_strength ?? null,
    time_window: nullableString(row.time_window),
    source_type: row.source_type ?? 'internal_study',
    source_ref: typeof row.source_ref === 'string' ? row.source_ref : '',
    source_period_start: nullableString(row.source_period_start),
    source_period_end: nullableString(row.source_period_end),
    location_ids: stringArray(row.location_ids),
    location_labels: stringArray(row.location_labels),
    anonymization: row.anonymization ?? 'anonymized',
    named_use_ok: namedUseOk,
    channels_allowed: stringArray(row.channels_allowed),
    value_areas: stringArray(row.value_areas),
    claim_limitations: stringArray(row.claim_limitations),
    public_slot: nullableString(row.public_slot),
    public_payload: row.public_payload && typeof row.public_payload === 'object' && !Array.isArray(row.public_payload)
      ? (row.public_payload as Record<string, unknown>)
      : null,
    expires_at: nullableString(row.expires_at),
    approved_by: nullableString(row.approved_by),
    approved_at: nullableString(row.approved_at),
    archived_at: nullableString(row.archived_at),
    archive_reason: nullableString(row.archive_reason),
    created_at: row.created_at,
    updated_at: row.updated_at,
    store_pulse: row.store_pulse && typeof row.store_pulse === 'object'
      && typeof (row.store_pulse as Record<string, unknown>).location_id === 'string'
      && typeof (row.store_pulse as Record<string, unknown>).as_of_week === 'string'
      ? { location_id: (row.store_pulse as Record<string, unknown>).location_id, as_of_week: (row.store_pulse as Record<string, unknown>).as_of_week }
      : null,
  });
}
